/**
 * Export Organogram dialog — parameterises the live organogram PDF export
 * (POST /identity/org/organogram/export.pdf).
 *
 * Mirrors the official "Eduvos Organogram" Visio pack: one A4-landscape page
 * per group (Function by default), boxes = Title – Grade / Incumbent /
 * Critical Role, vacancies highlighted — but generated from LIVE data with the
 * org page's facet vocabulary (campus, financial dimensions, worker type,
 * position type) plus box-content toggles.
 *
 * The download goes through the authed apiClient as a blob (same reason as
 * user photos: a bare link would carry no bearer and 401 in PROD).
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/services/apiClient';
import { FileDown, Loader2 } from 'lucide-react';
import { useRmTaxonomyOptions } from '@/hooks/useRmTaxonomyOptions';

export interface OrganogramExportInitial {
  hierarchy?: 'reporting' | 'structure' | 'roles';
  pageBy?: 'function' | 'department' | 'campus' | 'none';
  fn?: string;
  hrDept?: string;
  team?: string;
  gradeFrom?: string;
  gradeTo?: string;
  campus?: string;
  dimCampus?: string;
  dimDept?: string;
  dimFaculty?: string;
  workerTypes?: string[];
  positionTypes?: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-seed the dialog (e.g. from the on-screen Organogram's current view). */
  initial?: OrganogramExportInitial | null;
  campusOptions: string[];
  dimCampusOptions: string[];
  dimDeptOptions: string[];
  dimFacultyOptions: string[];
  workerTypeOptions: string[];
  positionTypeOptions: string[];
}

export const OrganogramExportDialog: React.FC<Props> = ({
  open, onOpenChange, initial,
  campusOptions, dimCampusOptions, dimDeptOptions, dimFacultyOptions,
  workerTypeOptions, positionTypeOptions,
}) => {
  const [hierarchy, setHierarchy] = useState<'reporting' | 'structure' | 'roles'>('reporting');
  const [pageBy, setPageBy] = useState<'function' | 'department' | 'campus' | 'none'>('function');
  const [fn, setFn] = useState('all');
  const [hrDept, setHrDept] = useState('all');
  const [team, setTeam] = useState('all');
  const [gradeFrom, setGradeFrom] = useState('all');
  const [gradeTo, setGradeTo] = useState('all');
  const [campus, setCampus] = useState('all');
  const [dimCampus, setDimCampus] = useState('all');
  const [dimDept, setDimDept] = useState('all');
  const [dimFaculty, setDimFaculty] = useState('all');
  const [workerTypes, setWorkerTypes] = useState<Set<string>>(() => new Set());
  const [positionTypes, setPositionTypes] = useState<Set<string>>(() => new Set());
  const [includeVacant, setIncludeVacant] = useState(true);
  const [showGrade, setShowGrade] = useState(true);
  const [showIncumbent, setShowIncumbent] = useState(true);
  const [showPn, setShowPn] = useState(false);
  const [showCritical, setShowCritical] = useState(true);
  const [typeAccents, setTypeAccents] = useState(true);
  const [busy, setBusy] = useState<null | 'pdf' | 'vsdx'>(null);
  const [error, setError] = useState<string | null>(null);

  // Adopt the caller's view state each time the dialog opens with one provided.
  React.useEffect(() => {
    if (!open || !initial) return;
    if (initial.hierarchy) setHierarchy(initial.hierarchy);
    if (initial.pageBy) setPageBy(initial.pageBy);
    if (initial.fn) setFn(initial.fn);
    if (initial.hrDept) setHrDept(initial.hrDept);
    if (initial.team) setTeam(initial.team);
    if (initial.gradeFrom) setGradeFrom(initial.gradeFrom);
    if (initial.gradeTo) setGradeTo(initial.gradeTo);
    if (initial.campus) setCampus(initial.campus);
    if (initial.dimCampus) setDimCampus(initial.dimCampus);
    if (initial.dimDept) setDimDept(initial.dimDept);
    if (initial.dimFaculty) setDimFaculty(initial.dimFaculty);
    if (initial.workerTypes) setWorkerTypes(new Set(initial.workerTypes));
    if (initial.positionTypes) setPositionTypes(new Set(initial.positionTypes));
  }, [open, initial]);

  const taxonomy = useRmTaxonomyOptions();

  const gradeSlice = React.useMemo(() => {
    const all = taxonomy.grades;
    if (!all.length || (gradeFrom === 'all' && gradeTo === 'all')) return [];
    const i = gradeFrom === 'all' ? 0 : all.indexOf(gradeFrom);
    const j = gradeTo === 'all' ? all.length - 1 : all.indexOf(gradeTo);
    if (i < 0 || j < 0) return [];
    const [lo, hi] = i <= j ? [i, j] : [j, i];
    return all.slice(lo, hi + 1);
  }, [taxonomy.grades, gradeFrom, gradeTo]);

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<string>>>) => (v: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });

  const handleExport = async (fmt: 'pdf' | 'vsdx' = 'pdf') => {
    setBusy(fmt);
    setError(null);
    try {
      const res = await apiClient.post<Blob>(`/identity/org/organogram/export.${fmt}`, {
        hierarchy,
        page_by: pageBy,
        filters: {
          function: fn === 'all' ? null : fn,
          hr_department: hrDept === 'all' ? null : hrDept,
          team: team === 'all' ? null : team,
          campus: campus === 'all' ? null : campus,
          dimension_campus: dimCampus === 'all' ? null : dimCampus,
          dimension_department: dimDept === 'all' ? null : dimDept,
          dimension_faculty: dimFaculty === 'all' ? null : dimFaculty,
          worker_types: Array.from(workerTypes),
          position_types: Array.from(positionTypes),
          grades: gradeSlice,
          include_vacant: includeVacant,
          vacant_only: false,
        },
        options: {
          show_grade: showGrade,
          show_incumbent: showIncumbent,
          show_pn: showPn,
          show_critical: showCritical,
          type_accents: typeAccents,
        },
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Eduvos Organogram ${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e: any) {
      // With responseType 'blob', an error body arrives as a Blob — parse it
      // or the server's detail message is never shown.
      let detail: string | undefined;
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try { detail = JSON.parse(await data.text())?.detail; } catch { /* not JSON */ }
      } else {
        detail = data?.detail;
      }
      setError(detail || e?.message || 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  const multiPick = (
    label: string, options: string[], selected: Set<string>, onToggle: (v: string) => void,
  ) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {options.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            className={
              'rounded border px-2 py-0.5 text-xs ' +
              (selected.has(o)
                ? 'border-heritage-blue bg-heritage-blue/10 text-heritage-blue'
                : 'border-border text-muted-foreground hover:bg-muted')
            }
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export organogram (PDF)</DialogTitle>
          <DialogDescription>
            Generated from live data in the official pack&apos;s format — one A4
            landscape page per group. PDF to print, .vsdx to keep editing in Visio.
            Empty filters export the whole organisation.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Hierarchy</Label>
              <Select value={hierarchy} onValueChange={(v) => setHierarchy(v as any)}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reporting">Reporting lines (people)</SelectItem>
                  <SelectItem value="roles">Roles by reporting line (titles &amp; counts)</SelectItem>
                  <SelectItem value="structure">Structure (roles &amp; counts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">One page per</Label>
              <Select value={pageBy} onValueChange={(v) => setPageBy(v as any)}
                      disabled={hierarchy === 'structure'}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="function">Function</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="campus">Campus</SelectItem>
                  <SelectItem value="none">Single chart (no grouping)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Function</Label>
              <Select value={fn} onValueChange={setFn}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All functions</SelectItem>
                  {taxonomy.functions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">HR department</Label>
              <Select value={hrDept} onValueChange={setHrDept}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All HR departments</SelectItem>
                  {taxonomy.hrDepartments.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Team (any level)</Label>
              <Select value={team} onValueChange={setTeam}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All teams</SelectItem>
                  {taxonomy.teams.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Grade from (senior)</Label>
              <Select value={gradeFrom} onValueChange={setGradeFrom}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Top</SelectItem>
                  {taxonomy.grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Grade to (junior)</Label>
              <Select value={gradeTo} onValueChange={setGradeTo}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Bottom</SelectItem>
                  {taxonomy.grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Campus</Label>
              <Select value={campus} onValueChange={setCampus}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campuses</SelectItem>
                  {campusOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fin. campus</Label>
              <Select value={dimCampus} onValueChange={setDimCampus}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fin. campuses</SelectItem>
                  {dimCampusOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fin. department</Label>
              <Select value={dimDept} onValueChange={setDimDept}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fin. departments</SelectItem>
                  {dimDeptOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Faculty</Label>
              <Select value={dimFaculty} onValueChange={setDimFaculty}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All faculties</SelectItem>
                  {dimFacultyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {multiPick('Worker type (filled seats)', workerTypeOptions, workerTypes, toggle(setWorkerTypes))}
          {multiPick('Position type (incl. vacant)', positionTypeOptions, positionTypes, toggle(setPositionTypes))}

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-border p-3">
            {[
              ['Include vacant seats', includeVacant, setIncludeVacant, false],
              ['Show grade', showGrade, setShowGrade, false],
              ['Show incumbent', showIncumbent, setShowIncumbent, hierarchy !== 'reporting'],
              ['Show personnel number', showPn, setShowPn, hierarchy !== 'reporting'],
              ['Show Critical Role', showCritical, setShowCritical, false],
              ['Position-type colour accents', typeAccents, setTypeAccents, false],
            ].map(([label, val, set, off]) => (
              <label key={label as string}
                     className={`flex items-center gap-2 text-sm ${off ? 'text-muted-foreground' : ''}`}>
                <Checkbox
                  checked={val as boolean}
                  disabled={off as boolean}
                  onCheckedChange={(c) => (set as React.Dispatch<React.SetStateAction<boolean>>)(c === true)}
                />
                {label as string}
              </label>
            ))}
          </div>

          {error && (
            <p className="rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => handleExport('vsdx')} disabled={!!busy}>
            {busy === 'vsdx'
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
              : <><FileDown className="mr-2 h-4 w-4" /> Download .vsdx</>}
          </Button>
          <Button onClick={() => handleExport('pdf')} disabled={!!busy}>
            {busy === 'pdf'
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
              : <><FileDown className="mr-2 h-4 w-4" /> Download PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
